import React, { useEffect, useRef } from 'react';

export const PixelSimulation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pixelSize = 6;
    const width = 40;
    const height = 60;
    canvas.width = width * pixelSize;
    canvas.height = height * pixelSize;

    gridRef.current = Array(height).fill().map(() => Array(width).fill(0));

    // Add a small wick at the bottom
    const wickX = Math.floor(width / 2);
    const wickY = height - 1;
    gridRef.current[wickY][wickX] = -1; // Mark as solid wick

    const animate = () => {
      // Generate flame at the wick
      const wickX = Math.floor(width / 2);
      const wickY = height - 2;
      
      // Realistic flame generation - concentrated at center
      if (Math.random() > 0.3) {
        gridRef.current[wickY][wickX] = Math.min(1, gridRef.current[wickY][wickX] + Math.random() * 0.4 + 0.3);
      }
      
      // Occasional small spread
      if (Math.random() > 0.85) {
        const offset = Math.random() > 0.5 ? 1 : -1;
        const fireX = wickX + offset;
        if (fireX >= 0 && fireX < width) {
          gridRef.current[wickY][fireX] = Math.min(1, gridRef.current[wickY][fireX] + Math.random() * 0.2);
        }
      }

      // Physics simulation with better containment
      for (let y = height - 2; y >= 0; y--) {
        for (let x = 0; x < width; x++) {
          if (gridRef.current[y][x] === -1) continue; // Skip candle pixels
          
          let heat = gridRef.current[y][x];
          
          if (heat > 0.01) {
            // Realistic fire physics with teardrop shape
            const currentHeight = (height - 1) - y;
            const normalizedHeight = currentHeight / 15; // Shorter flame normalization
            
            // Natural decay increases with height (less fuel at top)
            const heightDecay = 0.85 - (normalizedHeight * 0.2);
            const buoyancy = Math.max(0.1, 0.7 - (normalizedHeight * 0.4)); // Weaker buoyancy at height
            
            // Teardrop shape: wider and shorter
            const flameWidthFactor = Math.sin(Math.min(Math.PI, normalizedHeight * Math.PI)) * 1.2;
            const maxSpreadDistance = Math.max(1, Math.floor(5 * flameWidthFactor));
            
            // Move up with height-dependent buoyancy
            if (y > 0 && gridRef.current[y - 1][x] !== -1 && gridRef.current[y - 1][x] < 0.9) {
              const transfer = heat * buoyancy;
              gridRef.current[y - 1][x] = Math.min(1, gridRef.current[y - 1][x] + transfer);
              heat -= transfer * 0.5;
            }
            
            // Sideways spread based on flame shape
            const distanceFromWick = Math.abs(x - wickX);
            if (distanceFromWick <= maxSpreadDistance && Math.random() > 0.7) {
              const direction = Math.random() > 0.5 ? 1 : -1;
              const newX = x + direction;
              if (newX >= 0 && newX < width && gridRef.current[y][newX] !== -1 && gridRef.current[y][newX] < 0.5) {
                const spreadAmount = heat * 0.15 * flameWidthFactor;
                gridRef.current[y][newX] = Math.min(1, gridRef.current[y][newX] + spreadAmount);
              }
            }
            
            // Apply height-based decay
            gridRef.current[y][x] = heat * heightDecay;
            
            // Remove very weak flames
            if (gridRef.current[y][x] < 0.02) {
              gridRef.current[y][x] = 0;
            }
          }
        }
      }

      // Clear canvas
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Render pixels
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const heat = gridRef.current[y][x];
          
          if (heat === -1) {
            // Draw wick
            ctx.fillStyle = '#333333';
            ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
          } else if (heat > 0.01) {
            // Draw flame with enhanced color differentiation
            let r, g, b;
            
            if (heat > 0.95) {
              // Ultra-hot white core
              r = 255;
              g = 255;
              b = 255;
            } else if (heat > 0.85) {
              // Very hot white-yellow
              r = 255;
              g = 255;
              b = Math.floor(200 + (heat - 0.85) * 550);
            } else if (heat > 0.75) {
              // Hot bright yellow
              r = 255;
              g = 255;
              b = Math.floor(100 + (heat - 0.75) * 1000);
            } else if (heat > 0.6) {
              // Pure yellow
              r = 255;
              g = 255;
              b = Math.floor((heat - 0.6) * 667);
            } else if (heat > 0.45) {
              // Yellow-orange transition
              r = 255;
              g = Math.floor(200 + (heat - 0.45) * 367);
              b = Math.floor((heat - 0.45) * 133);
            } else if (heat > 0.3) {
              // Orange
              r = 255;
              g = Math.floor(120 + (heat - 0.3) * 533);
              b = 0;
            } else if (heat > 0.2) {
              // Orange-red
              r = 255;
              g = Math.floor(50 + (heat - 0.2) * 700);
              b = 0;
            } else if (heat > 0.1) {
              // Red-orange
              r = Math.floor(200 + (heat - 0.1) * 550);
              g = Math.floor((heat - 0.1) * 500);
              b = 0;
            } else if (heat > 0.05) {
              // Red
              r = Math.floor(150 + (heat - 0.05) * 1000);
              g = Math.floor((heat - 0.05) * 200);
              b = 0;
            } else {
              // Dark red
              r = Math.floor(100 + heat * 1000);
              g = Math.floor(heat * 100);
              b = 0;
            }
            
            ctx.fillStyle = `rgb(${Math.min(255, r)}, ${Math.min(255, g)}, ${Math.min(255, b)})`;
            ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100%',
      gap: '16px'
    }}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          border: '1px solid var(--bw)', 
          imageRendering: 'pixelated' 
        }} 
      />
      <div style={{ textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700' }}>Pixel-based Simulation</h3>
        <p style={{ margin: '0', opacity: '0.7', fontSize: '14px', fontFamily: 'Source Code Pro, monospace' }}>
	  Pixel simulation on Canvas 2D
        </p>
      </div>
    </div>
  );
};
